import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, BadgeCheck, ArrowUpRight } from "lucide-react";
import { currency } from "../lib/api";

export function ExpertCard({ expert }) {
  return (
    <Link to={`/experts/${expert.slug}`} data-testid={`expert-card-${expert.slug}`} className="tl-card p-5 flex flex-col group">
      <div className="flex items-start gap-4">
        <img src={expert.photo} alt={expert.name} className="w-16 h-16 rounded-full object-cover border-2 border-slate-200 group-hover:border-[#0891B2]/40 transition-colors shadow-[0_4px_14px_rgba(0,0,0,0.4)]" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="font-display font-semibold truncate">{expert.name}</h3>
            {expert.verified && <BadgeCheck className="w-4 h-4 text-[#0891B2] shrink-0" />}
          </div>
          <div className="text-xs text-[#475569] mt-0.5">{expert.professional_title}</div>
          <div className="flex items-center gap-3 mt-2 text-xs text-[#475569]">
            <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-[#0891B2] text-[#0891B2]" />{expert.rating_average?.toFixed(1) || "—"}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{expert.location}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-4">
        {(expert.skills || []).slice(0, 3).map((s) => <span key={s} className="tl-chip">{s}</span>)}
      </div>
      <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
        <div className="text-xs text-[#475569]">
          {expert.starting_price ? <>From <span className="text-slate-900 font-semibold">{currency(expert.starting_price)}</span></> : "Contact for quote"}
        </div>
        <span className="text-xs text-[#0891B2] flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowUpRight className="w-3 h-3" /></span>
      </div>
    </Link>
  );
}

export function PartnerCard({ partner }) {
  return (
    <Link to={`/experts/printing-partners/${partner.slug}`} data-testid={`partner-card-${partner.slug}`} className="tl-card p-5 group">
      <div className="aspect-[16/9] rounded-lg overflow-hidden mb-4 bg-[#FFFFFF] border border-slate-200">
        <img src={partner.photo} alt={partner.business_name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out" />
      </div>
      <div className="flex items-center gap-1.5">
        <h3 className="font-display font-semibold truncate">{partner.business_name}</h3>
        {partner.verified && <BadgeCheck className="w-4 h-4 text-[#0891B2] shrink-0" />}
      </div>
      <div className="text-xs text-[#475569] mt-1 flex items-center gap-3 flex-wrap">
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{partner.location}</span>
        <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-[#0891B2] text-[#0891B2]" />{partner.rating_average?.toFixed(1) || "—"}</span>
        {partner.distance_km != null && (
          <span data-testid="partner-distance" className="text-[#0891B2] font-medium">{partner.distance_km} km away</span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {(partner.technologies || []).slice(0, 4).map((t) => <span key={t} className="tl-chip">{t}</span>)}
      </div>
    </Link>
  );
}

export function ProductCard({ product }) {
  const slug = product.slug;
  return (
    <Link to={`/shop/product/${slug}`} data-testid={`product-card-${slug}`} className="tl-card overflow-hidden group flex flex-col">
      <div className="aspect-square overflow-hidden bg-[#FFFFFF] relative">
        <img src={(product.images || [])[0] || "https://images.unsplash.com/photo-1638959492386-f9a68d55c374?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"}
          onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1638959492386-f9a68d55c374?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"; }}
          alt={product.name} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500 ease-out" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="text-[10px] tl-label mb-1">{product.category}</div>
        <h3 className="text-sm font-semibold line-clamp-2 mb-1 group-hover:text-[#2563EB] transition-colors">{product.name}</h3>
        <div className="text-xs text-[#475569] mb-3">by {product.creator_name || "Trinity Layers"}</div>
        <div className="mt-auto flex items-center justify-between">
          <div className="font-display font-bold text-slate-900">
            {product.is_free ? <span className="text-[#0891B2]">FREE</span> : currency(product.price)}
          </div>
          <div className="flex items-center gap-1 text-xs text-[#475569]">
            <Star className="w-3 h-3 fill-[#0891B2] text-[#0891B2]" />
            {product.rating_average?.toFixed(1) || "—"}
          </div>
        </div>
      </div>
    </Link>
  );
}
